import { Permission } from '@interfaces/a-organizar/permission';
import { actionCode, classifyAction, describeAction, isMenuGroup } from './permission-action.utils';
import { groupObject, groupTitle } from './permission-labels';
import {
  OTHER_MODULE_ID,
  OTHER_MODULE_LABEL,
  PERMISSION_TREE_CONFIG,
  UNMAPPED_MENU_SCREEN_ID,
  UNMAPPED_MENU_SCREEN_LABEL,
} from './permission-tree.config';
import {
  PermissionAction,
  PermissionAreaNode,
  PermissionModuleConfig,
  PermissionModuleNode,
  PermissionScreenConfig,
  PermissionScreenNode,
} from './permission-tree.models';

/**
 * Monta a árvore Módulo › Área › Tela a partir do catálogo do papel (`GET permissions/all?role=`) e do mapa
 * estático (`PERMISSION_TREE_CONFIG`). Função pura: o mesmo catálogo dá sempre a mesma árvore.
 *
 * - Grupo que o papel não tem não aparece; tela sem nada para o papel some; área e módulo vazios somem.
 * - Grupo do catálogo fora do mapa vai para "Outras (fora do menu)" (uma tela por grupo). Nada some.
 */
export function buildPermissionTree(
  catalog: Permission[],
  config: PermissionModuleConfig[] = PERMISSION_TREE_CONFIG
): PermissionModuleNode[] {
  const groupsByName = new Map(catalog.map(group => [group.name, group]));

  const menuActions = new Map<string, PermissionAction>();
  catalog
    .filter(group => isMenuGroup(group.name))
    .forEach(group =>
      group.claims.forEach(claim => {
        menuActions.set(claim.value, {
          value: claim.value,
          label: 'Aparece no menu',
          // Nome do item de menu, usado só quando a claim não tem tela no mapa ("Outras (fora do menu)").
          hint: describeAction(claim, actionCode(claim, group.name)).label,
          kind: 'menu',
          groupName: group.name,
          groupLabel: group.description,
        });
      })
    );

  const groupHome = resolveGroupHomes(config, groupsByName, menuActions);
  const usedMenuValues = new Set<string>();
  const usedGroups = new Set<string>();

  const buildScreen = (
    screenConfig: PermissionScreenConfig,
    path: string[]
  ): PermissionScreenNode | null => {
    const sharedMenu = screenConfig.menuPermission
      ? (menuActions.get(screenConfig.menuPermission) ?? null)
      : null;
    if (sharedMenu) usedMenuValues.add(sharedMenu.value);
    // A descrição da claim de menu do backend ("Ver Controle > Endividamento > Empréstimos no menu") vira o caminho
    // com o mesmo separador do resto da tela.
    const menuAction: PermissionAction | null = sharedMenu
      ? { ...sharedMenu, hint: `Item ${[...path, screenConfig.label].join(' › ')} do menu lateral` }
      : null;

    const groupNames = (screenConfig.permissionGroups ?? []).filter(
      name => groupHome.get(name) === screenConfig.id && groupsByName.has(name)
    );
    groupNames.forEach(name => usedGroups.add(name));

    return createScreenNode(
      screenConfig.id,
      screenConfig.label,
      path,
      menuAction,
      groupNames.map(name => groupsByName.get(name)!)
    );
  };

  const modules: PermissionModuleNode[] = config
    .map(moduleConfig => {
      const children = moduleConfig.children
        .map(child => {
          if (child.kind === 'screen') return buildScreen(child, [moduleConfig.label]);

          const screens = child.screens
            .map(screenConfig => buildScreen(screenConfig, [moduleConfig.label, child.label]))
            .filter((node): node is PermissionScreenNode => !!node);
          if (!screens.length) return null;

          const areaNode: PermissionAreaNode = {
            kind: 'area',
            id: child.id,
            label: child.label,
            path: [moduleConfig.label],
            screens,
            values: screens.flatMap(item => item.values),
          };
          return areaNode;
        })
        .filter((node): node is PermissionAreaNode | PermissionScreenNode => !!node);

      return createModuleNode(moduleConfig.id, moduleConfig.label, children, false);
    })
    .filter(module => module.children.length > 0);

  const otherScreens = catalog
    .filter(group => !isMenuGroup(group.name) && !usedGroups.has(group.name))
    .map(group =>
      createScreenNode(`other-${group.name}`, groupTitle(group), [OTHER_MODULE_LABEL], null, [
        group,
      ])
    )
    .filter((node): node is PermissionScreenNode => !!node)
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

  const unmappedMenu = [...menuActions.values()].filter(
    action => !usedMenuValues.has(action.value)
  );
  if (unmappedMenu.length) {
    const values = unmappedMenu.map(action => action.value);
    otherScreens.push({
      kind: 'screen',
      id: UNMAPPED_MENU_SCREEN_ID,
      label: UNMAPPED_MENU_SCREEN_LABEL,
      path: [OTHER_MODULE_LABEL],
      menuAction: null,
      readActions: unmappedMenu.map(action => ({
        ...action,
        label: `Mostrar no menu: ${action.hint}`,
        hint: '',
        kind: 'read' as const,
      })),
      writeActions: [],
      groupNames: [...new Set(unmappedMenu.map(action => action.groupName))],
      values,
    });
  }

  if (otherScreens.length) {
    modules.push(createModuleNode(OTHER_MODULE_ID, OTHER_MODULE_LABEL, otherScreens, true));
  }

  return modules;
}

/**
 * Tela principal de cada grupo listado em mais de uma tela: a primeira (na ordem do mapa) cuja claim de menu o papel
 * tem; sem nenhuma, a primeira da lista.
 */
function resolveGroupHomes(
  config: PermissionModuleConfig[],
  groupsByName: Map<string, Permission>,
  menuActions: Map<string, PermissionAction>
): Map<string, string> {
  const candidates = new Map<string, PermissionScreenConfig[]>();
  config.forEach(moduleConfig =>
    moduleConfig.children
      .flatMap(child => (child.kind === 'area' ? child.screens : [child]))
      .forEach(screenConfig =>
        (screenConfig.permissionGroups ?? []).forEach(groupName => {
          if (!groupsByName.has(groupName)) return;
          candidates.set(groupName, [...(candidates.get(groupName) ?? []), screenConfig]);
        })
      )
  );

  const homes = new Map<string, string>();
  candidates.forEach((screens, groupName) => {
    const visible = screens.find(
      item => !!item.menuPermission && menuActions.has(item.menuPermission)
    );
    homes.set(groupName, (visible ?? screens[0]).id);
  });
  return homes;
}

function createScreenNode(
  id: string,
  label: string,
  path: string[],
  menuAction: PermissionAction | null,
  groups: Permission[]
): PermissionScreenNode | null {
  const readActions: PermissionAction[] = [];
  const writeActions: PermissionAction[] = [];

  groups.forEach(group =>
    group.claims.forEach(claim => {
      const code = actionCode(claim, group.name);
      const text = describeAction(claim, code, groupObject(group.name));
      const action: PermissionAction = {
        value: claim.value,
        label: text.label,
        hint: text.hint,
        kind: classifyAction(code),
        groupName: group.name,
        groupLabel: groupTitle(group),
      };
      (action.kind === 'read' ? readActions : writeActions).push(action);
    })
  );

  if (!menuAction && !readActions.length && !writeActions.length) return null;

  return {
    kind: 'screen',
    id,
    label,
    path,
    menuAction,
    readActions,
    writeActions,
    groupNames: groups.map(group => group.name),
    values: [
      ...(menuAction ? [menuAction.value] : []),
      ...readActions.map(action => action.value),
      ...writeActions.map(action => action.value),
    ],
  };
}

function createModuleNode(
  id: string,
  label: string,
  children: (PermissionAreaNode | PermissionScreenNode)[],
  isOther: boolean
): PermissionModuleNode {
  return {
    kind: 'module',
    id,
    label,
    isOther,
    children,
    values: children.flatMap(child => child.values),
  };
}
